import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
    LsqApiServiceListSource,
    LsqDataSource,
    SortDirectionEnterprise
} from '@lsq-frontend/angular-shared/api-service';
import {
    FeatureFlags,
    RouteParams,
    UserStates
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { UserStateService } from '@lsq-frontend/fasttrack-shared/features/user-state-service';
import {
    atlas,
    CUSTOMER_OVERVIEW,
    CUSTOMERS_REQUEST_CREDIT,
    INCREASE_CREDIT_LIMIT
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    AgingFilterType,
    FileType,
    GetRelationshipsByClientResponse,
    RelationshipInformation,
    UserStateStatus
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    ArInsightsReportType,
    GetCanDownloadInsightsReportRequest,
    GetCanDownloadInsightsReportResponse,
    GetCanDownloadInsightsReportService,
    GetInsightsReportDownloadUrlResponse,
    GetInsightsReportDownloadUrlService,
    GetRelationshipsByClientRequest,
    GetRelationshipsByClientService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    DownloadService,
    forLifeOf,
    Interaction,
    ITableColumn,
    LifecycleAware,
    ReportRequestUserSettings,
    Styling,
    TourHelperService,
    Utilities
} from '@lsq-frontend/common-ui';
import {
    FeatureFlagService,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService,
    Serializer
} from '@lsq-frontend/common-ui';
import { GuidedTour } from 'ngx-guided-tour';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first } from 'rxjs/operators';
import { AnalyticsValue } from '../../constants/analytics.constants';
import { TourAnalyticsNames, TourIds, TourSteps } from '../../constants/tours.constants';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';

@LifecycleAware()
@Component({
    selector: 'customers',
    template: `
        <div
            id="customers"
            [ngClass]="{ 'min-page-width-large-up': !(dataSource?.loading$ | async) }"
        >
            <lsq-banner
                [reportName]="(isSmallDeviceStream | async) ? null : title"
                [titleText]="(isSmallDeviceStream | async) ? title : null"
                [subTitle]="lastUpdatedDate | lastUpdatedDate"
                [hasContent]="true"
                [analyticsName]="analyticsName"
                [service]="getRelationshipsByClientService"
                [hideExport]="hideExport || !dataSource"
                [includeDateFilterStringPlaceholder]="false"
            >
                <ng-container *ngIf="!(isSmallDeviceStream | async)">
                    <lsq-button
                        class="add-new-customer-button"
                        [routerLink]="requestNewCustomerCreditLink"
                    >
                        Add New Customer
                    </lsq-button>
                </ng-container>
            </lsq-banner>

            <div class="container-fluid page-container" *ngIf="dataSource">
                <lsq-report-actions-layout layoutWidth="md">
                    <form id="customers-filters-form" [formGroup]="customersFiltersFormGroup">
                        <lsq-select
                            id="customers-filters-customers-filter"
                            [options]="customersFilterOptions"
                            [floatLabel]="true"
                            formControlName="customersFilter"
                        >
                        </lsq-select>
                        <div id="customers-filters-customer-name">
                            <lsq-input
                                placeholder="Enter Customer Name"
                                formControlName="customerName"
                                [name]="'customerName'"
                                [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                                [group]="customersFiltersFormGroup"
                            >
                            </lsq-input>
                        </div>
                    </form>
                </lsq-report-actions-layout>
                <div class="row">
                    <div class="col">
                        <lsq-data-table-mobile
                            *ngIf="responsiveService.smBelow | async"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [cardsAreExpandable]="false"
                        >
                            <ng-template #mobileCardHeader let-row="row">
                                <div
                                    class="mtc-header-content"
                                    (click)="handleMobileCardClick(row)"
                                >
                                    <div class="mtc-header-title">
                                        {{ row.debtorName }}
                                    </div>
                                    <div class="customers-mtc-basic-info row">
                                        <div class="col">
                                            <div class="d-flex flex-column">
                                                <span
                                                    class="customers-mtc-basic-info-header small-uppercase"
                                                >
                                                    Credit Limit
                                                </span>
                                                <span class="customers-mtc-basic-info-value">
                                                    {{ row.creditLimit | lsqCurrency }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="col">
                                            <div class="d-flex flex-column">
                                                <span
                                                    class="customers-mtc-basic-info-header small-uppercase"
                                                >
                                                    Credit Available
                                                </span>
                                                <span class="customers-mtc-basic-info-value">
                                                    {{ row.creditLimitAvailable | lsqCurrency }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <lsq-button
                                        appearance="transparent"
                                        [style.padding]="'1rem 1rem 1rem 0'"
                                        [queryParams]="{ key: row.debtorKey }"
                                        [routerLink]="increaseCreditLimitLink"
                                    >
                                        INCREASE CREDIT LIMIT
                                    </lsq-button>
                                </div>
                            </ng-template>
                        </lsq-data-table-mobile>

                        <lsq-data-table
                            *ngIf="!(responsiveService.smBelow | async)"
                            [dataSource]="dataSource"
                            [isPaginated]="true"
                            [hasStickyHeader]="true"
                            (takeChanged)="handleTakeChange($event)"
                            [columnVisibilities]="{
                                customerReport:
                                    (creditReportFeatureFlag$ | async) &&
                                    isValidClientForInsightReports
                            }"
                        >
                            <lsq-row-count-column></lsq-row-count-column>

                            <lsq-customer-info-column></lsq-customer-info-column>

                            <lsq-customer-credit-limit-column></lsq-customer-credit-limit-column>

                            <lsq-currency-column
                                [column]="{
                                    header: 'Unpaid Invoices',
                                    key: 'openInvoicesAmount'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [isMinZero]="true"
                                [column]="{
                                    header: 'Ineligibles',
                                    key: 'ineligibles'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [isNullZero]="true"
                                [column]="{
                                    header: 'Credit Available',
                                    key: 'creditLimitAvailable'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-currency-column
                                [isNullZero]="true"
                                [column]="{
                                    header: 'Over Credit Limit',
                                    key: 'creditLimitAmountOver'
                                }"
                            >
                            </lsq-currency-column>

                            <lsq-spacer-column width="2%"></lsq-spacer-column>

                            <lsq-percent-column
                                [isPercentWhole]="true"
                                [column]="{
                                    header: '% Used',
                                    key: 'creditLimitPercentUsed'
                                }"
                            >
                            </lsq-percent-column>

                            <lsq-percent-column
                                [isPercentWhole]="true"
                                [column]="{
                                    header: 'Dilution',
                                    key: 'dilution'
                                }"
                            >
                            </lsq-percent-column>

                            <lsq-date-column
                                [isDays]="true"
                                [column]="{
                                    header: 'AR Turn',
                                    key: 'averageDaysToPay'
                                }"
                            >
                            </lsq-date-column>
                            <lsq-column
                                [column]="{
                                    header: 'Credit Report',
                                    key: 'customerReport',
                                    width: '7rem',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <lsq-button
                                        [disabled]="
                                            customerReportButtonIsVisibleBound(row) == false
                                        "
                                        [ngStyle]="{ cursor: 'pointer', float: 'right' }"
                                        (buttonClick)="handleCustomerReportClick(row)"
                                        [color]="color.Green"
                                        [style.display]="'block'"
                                        appearance="transparent"
                                    >
                                        <ng-container
                                            *ngIf="customerReportButtonIsVisibleBound(row) == true"
                                        >
                                            <lsq-icon
                                                *ngIf="!loadingReports.includes(row?.debtorKey)"
                                                [type]="icons.Export"
                                                [color]="color.Green"
                                            >
                                            </lsq-icon>
                                            <lsq-circle-loader
                                                *ngIf="loadingReports.includes(row?.debtorKey)"
                                                class="report-circle-loader"
                                            ></lsq-circle-loader>
                                        </ng-container>
                                        <ng-container
                                            *ngIf="customerReportButtonIsVisibleBound(row) == false"
                                        >
                                            <lsq-icon [type]="icons.Export" [color]="color.Gray">
                                            </lsq-icon>
                                        </ng-container>
                                    </lsq-button>
                                </ng-template>
                            </lsq-column>
                        </lsq-data-table>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./customers.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CustomersComponent implements OnDestroy, OnInit {
    public title = 'Customers';
    public lastUpdatedDate: Date;
    public analyticsName = AnalyticsValue.Customers;
    public debounceTime = Interaction.SearchDebounceTimeMs;
    public isSmallDeviceStream: Observable<boolean>;
    public customersFiltersFormGroup = new FormGroup({
        customersFilter: new FormControl(AgingFilterType.WithBalances),
        customerName: new FormControl()
    });
    public customersFilterOptions = [
        { label: 'All Customers', value: AgingFilterType.All },
        { label: 'Customers With Balance', value: AgingFilterType.WithBalances },
        { label: 'Customers With No Balance', value: AgingFilterType.WithoutBalances },
        { label: 'Customers Over Credit Limit', value: AgingFilterType.OverCreditLimit },
        { label: 'Ineligible Customers', value: AgingFilterType.NoBuy },
        { label: 'Eligible Customers', value: AgingFilterType.Buy }
    ];
    public columns: ITableColumn[];
    public hideExport = false;
    public icons = Icons;
    public color = Color;
    public increaseCreditLimitLink = getRouterLinkUrlById(atlas, INCREASE_CREDIT_LIMIT);
    public requestNewCustomerCreditLink = getRouterLinkUrlById(atlas, CUSTOMERS_REQUEST_CREDIT);
    public featureFlags = FeatureFlags;
    private _componentIsAlive = false;
    private _userSettingsSavedRequest: ReportRequestUserSettings;
    private validDebtorKeysForCustomerReport: string[] = [];
    public canDownloadInsightsReportDataSource = new LsqDataSource<
        GetCanDownloadInsightsReportResponse
    >({
        data$: this._getCanDownloadInsightsReportService.dataStream,
        loading$: this._getCanDownloadInsightsReportService.isInFlightStream,
        error$: this._getCanDownloadInsightsReportService.errorStream
    });
    public isValidClientForInsightReports: boolean = false;
    public loadingReports: number[] = [];

    public dataSource: LsqApiServiceListSource<
        GetRelationshipsByClientResponse,
        GetRelationshipsByClientRequest
    >;

    public readonly customerReportButtonIsVisibleBound = this._customerReportButtonIsVisible.bind(
        this
    );

    constructor(
        public getRelationshipsByClientService: GetRelationshipsByClientService,
        public responsiveService: ResponsiveService,
        private _userStateService: UserStateService,
        private _router: Router,
        private _featureFlagsService: FeatureFlagService,
        private _tourHelperService: TourHelperService,
        public _serializer: Serializer,
        private _downloadService: DownloadService,
        private _getInsightsReportDownloadUrlService: GetInsightsReportDownloadUrlService,
        private _getCanDownloadInsightsReportService: GetCanDownloadInsightsReportService,
        private _userService: UserService
    ) {
        this.customersFiltersFormGroup.controls.customersFilter.valueChanges
            .pipe(
                forLifeOf(this),
                debounceTime(this.debounceTime),
                filter(
                    (newFilter: AgingFilterType) => newFilter !== this.dataSource.request.filterType
                )
            )
            .subscribe((customersFilter) => {
                this.dataSource.dispatchRequest({ filterType: customersFilter });
            });

        this.customersFiltersFormGroup.controls.customerName.valueChanges
            .pipe(
                forLifeOf(this),
                distinctUntilChanged(),
                debounceTime(this.debounceTime),
                filter(
                    (customerNameSearch) =>
                        customerNameSearch.length >= Interaction.MinTextSearchLength ||
                        customerNameSearch.length === 0
                )
            )
            .subscribe((customerNameSearch) => {
                this.dataSource.dispatchRequest({ searchFilter: customerNameSearch });
            });

        this.getRelationshipsByClientService.dataStream
            .pipe(forLifeOf(this))
            .subscribe((response: GetRelationshipsByClientResponse) => {
                this.hideExport = !response.rows.length;
            });

        this._getCanDownloadInsightsReportService.execute(
            new GetCanDownloadInsightsReportRequest({
                companyKey: this._getCompanyKey(),
                reportType: ArInsightsReportType.Customer
            })
        );
        this.canDownloadInsightsReportDataSource.data$.pipe(forLifeOf(this)).subscribe((resp) => {
            if (resp.isValidClient) {
                this.isValidClientForInsightReports = resp.isValidClient;
                this.validDebtorKeysForCustomerReport = resp.validDebtorKeys;
            }
        });

        // After service returns and we get the feature flag.
        combineLatest(
            this._featureFlagsService.isOn(FeatureFlags.CustomersTour),
            this.responsiveService.smBelow,
            this.getRelationshipsByClientService.dataStream
        )
            .pipe(first())
            .subscribe(([featureFlag, isMobile, customersResponse]) => {
                if (featureFlag && !isMobile && customersResponse) {
                    const tourSteps = TourSteps.createCustomerTourSteps(
                        customersResponse.rows && !!customersResponse.rows.length
                    );
                    const customersTour: GuidedTour = this._tourHelperService.attachEndEvents(
                        {
                            tourId: TourIds.CustomersIntro,
                            minimumScreenSize: Styling.mdStart,
                            steps: tourSteps
                        },
                        TourAnalyticsNames.CustomersIntro
                    );

                    // Let the page render before starting tour.
                    Utilities.executeAfterRender(() => {
                        if (this._componentIsAlive) {
                            this._tourHelperService.startTour(customersTour);
                        }
                    });
                }
            });
    }

    public async ngOnInit(): Promise<void> {
        this._componentIsAlive = true;
        this.isSmallDeviceStream = this.responsiveService.smBelow.pipe(distinctUntilChanged());

        this.getRelationshipsByClientService.dataStream
            .pipe(forLifeOf(this))
            .subscribe((response) => {
                this.lastUpdatedDate = response.lastUpdatedDate;
            });

        const userState = await this._userStateService.getUserState(UserStates.GetCustomersRequest);

        let userSettings: string;
        if (userState) {
            userSettings = userState.jsonData;
        }

        this._userSettingsSavedRequest = userSettings
            ? (this._serializer.deserialize(userSettings) as ReportRequestUserSettings)
            : {};

        const initialRequest = new GetRelationshipsByClientRequest({
            sortColumn: 'debtorName',
            sortDirection: SortDirectionEnterprise.Asc,
            take: this._userSettingsSavedRequest.take,
            filterType: AgingFilterType.WithBalances
        });

        this.dataSource = new LsqApiServiceListSource<
            GetRelationshipsByClientResponse,
            GetRelationshipsByClientRequest
        >(this.getRelationshipsByClientService, initialRequest);
    }

    public ngOnDestroy(): void {
        this._componentIsAlive = false;
    }

    public handleMobileCardClick(row: RelationshipInformation): void {
        this._router.navigateByUrl(
            getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW).replace(
                RouteParams.DebtorKey,
                `${row.debtorKey}`
            )
        );
    }

    public handleTakeChange(newTake: number): void {
        // tslint:disable-next-line: no-floating-promises
        this._userStateService.createOrUpdateUserState(
            UserStates.GetCustomersRequest,
            UserStateStatus.Done, // TODO: remove when BE makes optional.
            { take: newTake }
        );
    }

    public creditReportFeatureFlag$ = this._featureFlagsService.isOn(
        FeatureFlags.ArInsightsReportDownloads
    );

    private _customerReportButtonIsVisible(row: RelationshipInformation): boolean {
        if (
            this.creditReportFeatureFlag$ &&
            this.isValidClientForInsightReports &&
            this.validDebtorKeysForCustomerReport &&
            this.validDebtorKeysForCustomerReport.includes(row.debtorKey.toString())
        ) {
            return true;
        }
        return false;
    }

    public async handleCustomerReportClick(row: RelationshipInformation): Promise<void> {
        this.loadingReports.push(row.debtorKey);
        const customerReportDownloadUrl: GetInsightsReportDownloadUrlResponse = await this._getInsightsReportDownloadUrlService.executeOnce(
            {
                companyKey: this._getCompanyKey(),
                debtorKey: row.debtorKey.toString(),
                reportType: ArInsightsReportType.Customer
            }
        );
        if (customerReportDownloadUrl.code !== 200) {
            const debtorKeyIdx = this.loadingReports.indexOf(row.debtorKey);
            if (debtorKeyIdx > -1) {
                this.loadingReports.splice(debtorKeyIdx, 1);
            }
            return;
        }

        await this._downloadService
            .executeUrl(
                customerReportDownloadUrl.presignedUrl,
                FileType.Pdf,
                customerReportDownloadUrl.fileName
            )
            .catch((err) => console.log(err));
        const debtorKeyIdx = this.loadingReports.indexOf(row.debtorKey);
        if (debtorKeyIdx > -1) {
            this.loadingReports.splice(debtorKeyIdx, 1);
        }
    }

    private _getCompanyKey(): string {
        return this._userService.getCompanyKey();
    }
}
